import { NgClass, NgIf } from '@angular/common';
import { Component, Input, ViewChild, ViewEncapsulation } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { Sort } from '@angular/material/sort';
import { ActivatedRoute, Router } from '@angular/router';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import moment from 'moment';
import { Observable, filter, forkJoin, of, race, switchMap, take, takeUntil } from 'rxjs';
import { FORMLY_FIELD_TYPES, HubStatus } from '@app/constants';
import {
	AuthService,
	AuthenticationInfo,
	DialogOptions,
	DialogService,
	FeedbackService,
	LoadingService,
	ReportService,
	SocketService,
} from '@app/core/services';
import { UnitControlInfoKey } from '@app/enums';
import { BulkAction, CellTransform, ColumnFilter, Filter, FilterConfig, SelectOption, TableColumn, TableConfig } from '@app/interfaces';
import { BaseListComponent, TableComponent } from '@app/shared/components';
import { TitleComponent } from '@app/shared/components/title/title.component';
import { FiltersComponent } from '@app/shared/forms/components/filters/filters.component';
import { FormComponent } from '@app/shared/forms/components/form.component';
import { scrollToToolBar } from '@app/shared/forms/forms.helper';
import { UnitService } from '@app/shared/services';
import { BankService } from '@app/shared/services/bank.service';
import { HubData } from '@app/shared/sockets/shared/interfaces';
import { isNil, mapArrayToSelectOptions } from '@app/utils';
import * as Transforms from '@app/utils/transforms';
import { ProcessBy, ProcessByDescription } from './enums/void-payments-process-by.enum';
import { ProcessMethod, ProcessMethodDescription } from './enums/void-payments-process-method.enum';
import { PaymentEntity, PaymentResultModel } from './interfaces/payment-entity.model';
import { PaymentParam } from './interfaces/payment-param.model';
import { VoidPaymentsService } from './services/void-payments.service';

const responsiveSize2 = 'col-xs-12 col-sm-6 col-md-4 col-lg-2';
const responsiveSize3 = 'col-xs-12 col-sm-6 col-md-4 col-lg-3';

@Component({
	selector: 'sl-void-payments',
	templateUrl: './void-payments.component.html',
	styleUrls: ['./void-payments.component.less'],
	encapsulation: ViewEncapsulation.None,
	standalone: true,
	imports: [TitleComponent, FormComponent, NgIf, MatCardModule, NgClass, FiltersComponent, TableComponent],
})
export class VoidPaymentsComponent extends BaseListComponent<PaymentEntity, VoidPaymentsService> {
	@Input() schoolAccounting = false;
	@ViewChild(TableComponent) paymentsTable: TableComponent;

	entityName = 'Void Payments';
	pageTitle = this.entityName;
	listingPagePath: string;
	ignoreGetList = true;

	hasPayrollAccess: boolean;
	isGlobalAdmin: boolean;
	isReadOnly: boolean;
	isVcCcEnabled = false;
	showTable = false;
	noDataMessage = 'No Payments Found';
	private paymentResults: PaymentEntity[] = [];
	private readonly now = new Date();

	commentForm = new UntypedFormGroup({});
	commentOptions: FormlyFormOptions = {
		formState: {
			mode: 'edit',
		},
	};

	commentFields: FormlyFieldConfig[] = [
		{
			key: 'Comment',
			type: FORMLY_FIELD_TYPES.textarea,
			className: 'row',
			props: {
				label: 'Reason for void',
				rows: 4,
				maxLength: 50,
			},
		},
	];

	listModel = {
		BankId: null,
		AchDisplayName: 'N/A (ACH Selected)',
		ProcessBy: ProcessBy.Vendor,
		ProcessMethod: ProcessMethod.CheckEft,
		PaymentDateFrom: new Date(this.now.getFullYear(), this.now.getMonth() - 1, this.now.getDate()),
		PaymentDateTo: new Date(this.now.getFullYear(), this.now.getMonth(), this.now.getDate()),
	} as PaymentParam;

	processModel = {
		VoidDate: new Date(this.now.getFullYear(), this.now.getMonth(), this.now.getDate()),
		Comment: '',
		ReinstateInvoices: true,
	};

	form = new UntypedFormGroup({});
	processForm = new UntypedFormGroup({});

	// These options are shared by both forms above, though the processForm doesn't use the selects.
	formOptions: FormlyFormOptions = {
		formState: {
			mode: 'edit',
			selects: {
				banks: [],
				processBys: [
					{ value: ProcessBy.Vendor, label: ProcessByDescription.Vendor },
					{ value: ProcessBy.Employee, label: ProcessByDescription.Employee },
				],
				processMethods: [
					{ value: ProcessMethod.CheckEft, label: ProcessMethodDescription.CheckEft },
					{ value: ProcessMethod.Ach, label: ProcessMethodDescription.Ach },
				],
			},
		},
	};
	formDataFilterFields: FormlyFieldConfig[] = this.getDataFilterFields();

	formProcessingFields: FormlyFieldConfig[] = this.getProcessingFields();

	filterOptions: FormlyFormOptions = {
		formState: {
			selects: {
				vendors: [],
				employees: [],
			},
		},
	};

	filterConfig: FilterConfig<PaymentEntity>[] = [
		{
			key: 'Vendor',
			transform: (fcFilter: Filter): string =>
				this.filterOptions.formState.selects.vendors.find((t: SelectOption) => t.value === fcFilter.value)?.label,
		},
		{
			key: 'Employee',
			transform: (fcFilter: Filter): string =>
				this.filterOptions.formState.selects.employees.find((t: SelectOption) => t.value === fcFilter.value)?.label,
		},
	];
	filterFields: FormlyFieldConfig[] = this.getTableFilterFields();

	filters = { search: '' };

	config: TableConfig<PaymentEntity> = {
		columns: this.tableColumns,
		bulkActions: this.bulkActions,
		defaultSortField: 'VendorName',
		defaultSortDirection: 'asc',
		showRowSelect: true,
		showRowActions: false,
		editableRows: false,
		onRowClick: null,
		selectedId: null,
	};

	constructor(
		protected readonly listService: VoidPaymentsService,
		protected readonly dialog: MatDialog,
		protected readonly feedbackService: FeedbackService,
		protected readonly route: ActivatedRoute,
		protected readonly router: Router,
		protected readonly reportService: ReportService,
		protected readonly authService: AuthService,
		protected readonly bankService: BankService,
		protected readonly unitService: UnitService,
		protected readonly dialogService: DialogService,
		protected readonly loadingService: LoadingService,
		protected readonly socketService: SocketService
	) {
		super(dialog, feedbackService, route, router, reportService);
	}

	protected afterOnInit(): void {
		this.isReadOnly = this.authService.isReadOnlyByLink(this.router.url);
		this.setAuthenticationInfo();
		this.loadDependencies();
	}

	protected beforeOnDestroy(): void {
		this.hideLoadingService();
	}

	setAuthenticationInfo() {
		this.authService.authentication$.pipe(takeUntil(this.destroy$)).subscribe((authentication: AuthenticationInfo) => {
			if (!authentication) {
				return;
			}

			this.isGlobalAdmin = authentication.isGA;
			this.hasPayrollAccess = false;
		});
	}

	private loadDependencies(): void {
		const banks$ = this.bankService.getBanks(false, true);
		const enabledVcCc$ = this.unitService.getValue(UnitControlInfoKey.EnabledVirtualCreditCards);

		forkJoin({ bankData: banks$, enabledVcCc: enabledVcCc$ }).subscribe((data: any) => {
			const selects = this.formOptions.formState.selects;

			selects.banks = mapArrayToSelectOptions(
				data.bankData.banks.map((m: any) => ({ Id: m.Id, Description: m.DisplayName })),
				{ value: 'Id', label: 'Description', all: false, sort: 'label' }
			);

			// Move the default bank to the top of the list.
			if (data?.bankData?.defaultBankId > 0) {
				const defaultBank = selects.banks.find((f: any) => f.value === data?.bankData?.defaultBankId);
				const index = selects.banks.indexOf(defaultBank);
				selects.banks.splice(index, 1);
				selects.banks.unshift(defaultBank);

				this.listModel.BankId = data.bankData.defaultBankId;
			}

			if (data.enabledVcCc.isValid) {
				this.isVcCcEnabled = data.enabledVcCc.value.toLowerCase() === 'true';
			}

			this.formDataFilterFields = this.getDataFilterFields();

			this.afterLoadDependencies();
		});
	}

	private afterLoadDependencies(): void {
		this.loadFilterLists();
		this.loadTableData();
	}

	private loadFilterLists(): void {
		this.data$.pipe(takeUntil(this.destroy$)).subscribe((data: PaymentEntity[]) => {
			this.filterOptions.formState.selects = {
				vendors: mapArrayToSelectOptions<PaymentEntity>(data, {
					value: 'VendorId',
					label: (value): string => `${value.VendorShortName} - ${value.VendorName}`,
					order: 'asc',
					sort: 'label',
					all: false,
				}),
				employees: mapArrayToSelectOptions<PaymentEntity>(data, {
					value: 'EmployeeId',
					label: (value): string => `${value.EmployeeName} (${value.EmployeeNumber})`,
					order: 'asc',
					sort: 'label',
					all: false,
				}),
			};
		});
	}

	getDataFilterFields(): FormlyFieldConfig[] {
		return [
			{
				fieldGroupClassName: 'row',
				fieldGroup: [
					{
						fieldGroupClassName: 'row',
						fieldGroup: [
							{
								className: responsiveSize3,
								key: 'BankId',
								type: FORMLY_FIELD_TYPES.select,
								templateOptions: {
									label: 'Bank',
									change: () => this.loadTableData(),
								},
								expressionProperties: {
									'templateOptions.required': (model: any): boolean => model?.ProcessMethod !== ProcessMethod.Ach,
									'templateOptions.options': 'formState.selects.banks',
									'templateOptions.label': () => (isNil(this.listModel?.BankId) ? 'Select a Bank' : 'Bank'),
								},
								hideExpression: (model: any): boolean => model?.ProcessMethod === ProcessMethod.Ach,
							},
							{
								className: responsiveSize3,
								key: 'AchDisplayName',
								type: FORMLY_FIELD_TYPES.input,
								templateOptions: {
									label: 'Bank',
								},
								expressionProperties: {
									'templateOptions.disabled': (model: any) => model?.ProcessMethod === ProcessMethod.Ach,
								},
								hideExpression: (model: any): boolean => model?.ProcessMethod !== ProcessMethod.Ach,
							},
							{
								className: responsiveSize2,
								key: 'ProcessBy',
								type: FORMLY_FIELD_TYPES.select,
								templateOptions: {
									label: 'Process By',
									required: true,
									change: () => this.refreshTable(),
								},
								expressionProperties: {
									'templateOptions.options': 'formState.selects.processBys',
								},
								hide: !this.hasPayrollAccess,
							},
							{
								className: responsiveSize2,
								key: 'ProcessMethod',
								type: FORMLY_FIELD_TYPES.select,
								templateOptions: {
									label: 'Process Method',
									required: true,
									change: () => this.loadTableData(),
								},
								expressionProperties: {
									'templateOptions.options': 'formState.selects.processMethods',
								},
								hide: this.schoolAccounting,
							},
							{
								className: responsiveSize2,
								key: 'PaymentDateFrom',
								type: FORMLY_FIELD_TYPES.datepicker,
								templateOptions: {
									label: 'Payment Date From',
									required: true,
									datepickerOptions: {
										dateChange: () => this.loadTableData(),
									},
								},
								expressionProperties: {
									'templateOptions.datepickerOptions.max': 'model.PaymentDateTo',
								},
							},
							{
								className: responsiveSize2,
								key: 'PaymentDateTo',
								type: FORMLY_FIELD_TYPES.datepicker,
								templateOptions: {
									label: 'Payment Date To',
									required: true,
									datepickerOptions: {
										dateChange: () => this.loadTableData(),
									},
								},
								expressionProperties: {
									'templateOptions.datepickerOptions.min': 'model.PaymentDateFrom',
								},
							},
						],
					},
				],
			},
		];
	}

	get tableColumns(): TableColumn<PaymentEntity>[] {
		return [
			{
				key: 'VendorShortName',
				header: 'Vendor Code',
				hidden: () => this.listModel.ProcessBy === ProcessBy.Employee,
			},
			{
				key: 'VendorName',
				header: 'Vendor Name',
				hidden: () => this.listModel.ProcessBy === ProcessBy.Employee,
			},
			{
				key: 'EmployeeName',
				header: 'Employee Name',
				transform: ({ row }: CellTransform<PaymentEntity>) => `${row.EmployeeName} (${row.EmployeeNumber})`,
				hidden: () => this.listModel.ProcessBy === ProcessBy.Vendor,
			},
			{
				key: 'PostDT',
				header: 'Payment Date',
				transform: ({ value }) => Transforms.toShortDate(value),
			},
			{
				key: 'CheckOrDepositNo',
				header: 'Check/EFT #',
				transform: ({ row }: CellTransform<PaymentEntity>) => `${row.RecordType} - ${row.CheckOrDepositNo}`,
			},
			{
				key: 'PostAmount',
				header: 'Amount',
				transform: ({ value }) => Transforms.formatAsCurrency(value),
			},
			{
				header: 'Vendor',
				key: 'Vendor',
				hidden: true,
				filter: ({ row, filterValue }: ColumnFilter<PaymentEntity>): boolean => row.VendorId === filterValue,
			},
			{
				header: 'Employee',
				key: 'Employee',
				hidden: true,
				filter: ({ row, filterValue }: ColumnFilter<PaymentEntity>): boolean => row.EmployeeId === filterValue,
			},
		];
	}

	get bulkActions(): BulkAction<any>[] {
		if (this.isReadOnly) {
			return [];
		}

		return [
			{
				label: 'Void Selected',
				onClick: (selection: PaymentEntity[]) => this.voidPayments(selection),
			},
		];
	}

	get tableHasSelections(): boolean {
		return !isNil(this.paymentsTable) && this.paymentsTable.numSelected > 0;
	}

	getProcessingFields(): FormlyFieldConfig[] {
		return [
			{
				fieldGroupClassName: 'row',
				fieldGroup: [
					{
						className: responsiveSize2,
						key: 'VoidDate',
						type: FORMLY_FIELD_TYPES.datepicker,
						templateOptions: {
							label: 'Void Date',
							required: true,
						},
					},
					{
						className: responsiveSize2,
						key: 'ReinstateInvoices',
						type: FORMLY_FIELD_TYPES.checkbox,
						templateOptions: {
							label: 'Reinstate Invoices',
							appearance: 'standard',
						},
						hideExpression: (): boolean => this.listModel.ProcessMethod !== ProcessMethod.CheckEft,
					},
				],
			},
		];
	}

	getTableFilterFields(): FormlyFieldConfig[] {
		return [
			{
				fieldGroupClassName: 'void-payments-filter-container',
				fieldGroup: [
					{
						fieldGroupClassName: 'row',
						fieldGroup: [
							{
								key: 'Vendor',
								className: 'col-xs-12 linq-mt-3',
								type: FORMLY_FIELD_TYPES.select,
								templateOptions: {
									label: 'Vendor',
								},
								expressionProperties: {
									'templateOptions.options': 'formState.selects.vendors',
								},
								hideExpression: (): boolean => this.listModel?.ProcessBy !== ProcessBy.Vendor,
							},
							{
								key: 'Employee',
								className: 'col-xs-12 linq-mt-3',
								type: FORMLY_FIELD_TYPES.select,
								templateOptions: {
									label: 'Employee',
								},
								expressionProperties: {
									'templateOptions.options': 'formState.selects.employees',
								},
								hideExpression: (): boolean => this.listModel?.ProcessBy !== ProcessBy.Employee,
							},
						],
					},
				],
			},
		];
	}

	private loadTableData(): void {
		this.feedbackService.clearAll();
		this.setTableData([]);
		if (!this.validatePaymentParameterModel()) {
			return;
		}

		this.listService.populateList(this.listModel).subscribe((response: PaymentResultModel) => {
			if (response.isValid === false) {
				this.feedbackService.error(response.errors);
				scrollToToolBar();
				return;
			}

			this.showTable = true;
			this.setTableData(response.checks);

			this.updateTableConfig();
			this.clearFilters();
			this.applyFilter(this.filters);
		});
	}

	private setTableData(data: PaymentEntity[]): void {
		const filteredData =
			data.length === 0
				? []
				: this.listModel.ProcessBy === ProcessBy.Employee
				? data.filter((f) => f.EmployeeId > 0)
				: data.filter((f) => f.VendorId > 0);

		this.paymentResults = data;
		this.data = filteredData;
		this.data$.next(this.data);
	}

	private updateTableConfig() {
		this.config = {
			...this.config,
			columns: this.tableColumns,
			bulkActions: this.bulkActions,
			showRowSelect: !this.isReadOnly,
			defaultSortField: this.listModel.ProcessBy === ProcessBy.Employee ? 'EmployeeName' : 'VendorName',
		};
	}

	private refreshTable(): void {
		this.setTableData(this.paymentResults);
		this.updateTableConfig();
		this.clearFilters();
		this.applyFilter(this.filters);

		// Make sure the sort in the table is reset to the default sort.
		const defaultSort = { active: this.config.defaultSortField, direction: this.config.defaultSortDirection } as Sort;
		this.paymentsTable.updateSort(defaultSort);
		this.paymentsTable.updateTableData();
	}

	private validatePaymentParameterModel(): boolean {
		let isValid = true;

		if (
			isNil(this.listModel.ProcessBy) ||
			isNil(this.listModel.ProcessMethod) ||
			isNil(this.listModel.PaymentDateFrom) ||
			isNil(this.listModel.PaymentDateTo) ||
			this.listModel.PaymentDateFrom > this.listModel.PaymentDateTo ||
			(this.listModel.ProcessMethod !== ProcessMethod.Ach && isNil(this.listModel.BankId))
		) {
			isValid = false;
		}

		if (!isValid) {
			this.form.markAllAsTouched();
			this.form.updateValueAndValidity();
		}

		return isValid;
	}

	private voidPayments(selection: PaymentEntity[]): void {
		this.feedbackService.clearAll();

		if (!this.validateProcessParameters()) {
			return;
		}

		if (!this.validateSelectedPayments(selection)) {
			return;
		}

		const title = 'Void Payment' + (selection.length > 1 ? 's' : '');
		this.getVoidMessage(selection)
			.pipe(
				switchMap((message: string) => {
					const dialogOptions = {
						title,
						message,
						okText: 'Void',
						form: this.commentForm,
						fields: this.commentFields,
						options: this.commentOptions,
						model: this.processModel,
					} as DialogOptions;

					return this.dialogService.open(dialogOptions);
				})
			)
			.pipe(
				switchMap((confirmed: boolean) => {
					if (confirmed) {
						this.loadingService.show();
						return this.listService.voidPayments(
							this.listModel.ProcessMethod,
							selection.map((m) => m.Id),
							this.processModel.VoidDate,
							this.processModel.ReinstateInvoices,
							this.processModel.Comment
						);
					}

					return of(null);
				})
			)
			.pipe(
				switchMap((processId?: any) => {
					if (!isNil(processId)) {
						this.processModel.Comment = '';
						return race(
							this.socketService.error$.pipe(
								takeUntil(this.destroy$),
								filter((notification: HubData) => notification.Id === processId),
								take(1)
							),
							this.socketService.success$.pipe(
								takeUntil(this.destroy$),
								filter((notification: HubData) => notification.Id === processId),
								take(1)
							)
						);
					}

					return of(null);
				})
			)
			.subscribe((result?: HubData) => {
				this.hideLoadingService();
				if (!isNil(result)) {
					if (result.Status === HubStatus.Success) {
						this.feedbackService.customSuccess(`${result?.Message?.Title} finished successfully!`);
						this.loadTableData();
					} else {
						this.feedbackService.error(`${result?.Message?.Title} failed!`);
						scrollToToolBar();
					}
				}
			});
	}

	private validateProcessParameters(): boolean {
		this.feedbackService.clearAll();
		if (!this.processForm.valid) {
			this.feedbackService.error('One or more required settings are invalid!');
			this.form.markAllAsTouched();
			scrollToToolBar();
		}

		return !this.processForm.invalid;
	}

	private validateSelectedPayments(selection: PaymentEntity[]): boolean {
		let validation = this.validatePayrollInvoices(selection);
		if (validation.isValid) {
			validation = this.validateVoidDate(selection);
		}

		if (validation.isValid) {
			return true;
		}

		this.dialogService.open({
			title: 'Void Payments Validation Error',
			message: validation.message,
			okText: 'OK',
			showCancel: false,
		} as DialogOptions);

		return false;
	}

	private validatePayrollInvoices(selection: PaymentEntity[]): { message: string; isValid: boolean } {
		const output = { message: '', isValid: true };
		if (this.isGlobalAdmin || this.processModel.ReinstateInvoices) {
			return output;
		}

		const prInvoices = selection.filter((f) => f.IsPayrollInvoice).map((m) => m.CheckOrDepositNo.toString());
		if (prInvoices.length > 0) {
			output.message = `Check/EFT number${prInvoices.length > 1 ? 's' : ''} ${prInvoices.join(', ')} ${
				prInvoices.length > 1 ? 'include payroll invoices' : 'includes a payroll invoice'
			}.<br>Please contact LINQ ERP NC Support if ${prInvoices.length > 1 ? 'these invoices' : 'this invoice'} should not be reinstated.`;
			output.isValid = false;
		}

		return output;
	}

	private validateVoidDate(selection: PaymentEntity[]): { message: string; isValid: boolean } {
		const output = { message: '', isValid: true };

		// PostDT needs to be UTC to compare with the VoidDate.
		const invalidDates = selection.filter((f) => moment(f.PostDT).utc().isAfter(moment(this.processModel.VoidDate), 'day'));
		if (invalidDates.length > 0) {
			output.message = `One or more selected items have a post date greater than the void date: ${Transforms.toShortDate(
				this.processModel.VoidDate
			)}.`;
			output.isValid = false;
		}

		return output;
	}

	private getVoidMessage(selection: PaymentEntity[]): Observable<string> {
		return this.listService.getVcCcFlags(selection.map((m) => m.Id)).pipe(
			switchMap((cardPaymentFlags: any) => {
				const paymentText = `${selection.length} Payment${selection.length > 1 ? 's' : ''} will be voided.`;
				const reinstateText =
					this.listModel.ProcessMethod === ProcessMethod.CheckEft
						? `<br>Invoice${selection.length > 1 ? 's' : ''} will ${this.processModel.ReinstateInvoices ? '' : 'NOT '}be reinstated.`
						: '';

				let vcCcPaymentText = '';
				if (cardPaymentFlags.includesCcCheck || cardPaymentFlags.includesVcCheck) {
					const ccText = 'Credit Card';
					const vcText = 'Virtual Card invoice';
					const hasBoth = cardPaymentFlags.includesCcCheck && cardPaymentFlags.includesVcCheck;
					const paymentType = hasBoth
						? `both ${ccText} and ${vcText} payments`
						: `a ${cardPaymentFlags.includesCcCheck ? ccText : vcText} payment`;

					vcCcPaymentText = `<br>This selection ${selection.length > 1 || hasBoth ? 'includes' : 'is'} ${paymentType}.`;
				}

				const message = `${paymentText}${reinstateText}${vcCcPaymentText}`;

				return of(message);
			})
		);
	}

	protected hideLoadingService(): void {
		if (this.loadingService.loading$.getValue() === true) {
			this.loadingService.hide();
		}
	}
}
